/**
 * FABTEXS AI — shared buyer notification plumbing.
 *
 * Both buyer surfaces alert on the same feed:
 *   • BuyerHome (`/me`)        — the bell, badge and panel
 *   • CollectionView (`/c/:id`) — the catalogue/chat screen the buyer actually
 *                                sits on, which previously had NO alert layer
 *                                at all, so an app-open buyer saw nothing.
 *
 * They therefore MUST share one dedup set and one chime, otherwise the same
 * notification would chime twice or replay after navigating between them.
 * Only one of the two is ever mounted at a time (separate routes), and the
 * seen-set is persisted, so a notification alerts exactly once per device.
 */

export const SEEN_KEY = 'fabtexs.buyer.seen_notifs';
export const POLL_MS = 12000;
// Original soft/soothing bell — the DEFAULT buyer tone. Deliberately NOT the
// seller ka-ching; the two sounds stay distinct.
export const CHIME_URL = '/sounds/buyer_chime.wav';

const readLS = (k) => { try { return localStorage.getItem(k); } catch (_e) { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch (_e) { /* private mode */ } };

export const loadSeen = () => {
  try { return new Set(JSON.parse(readLS(SEEN_KEY) || '[]')); } catch (_e) { return new Set(); }
};

export const saveSeen = (set) => {
  try { writeLS(SEEN_KEY, JSON.stringify([...set].slice(-500))); } catch (_e) { /* noop */ }
};

/** True when running as the installed PWA (standalone display-mode). */
export const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
  } catch (_e) { return false; }
};

/**
 * WebAudio chime player. Respects the browser autoplay policy: nothing plays
 * until `unlock()` has run inside a real user gesture. This is the app-OPEN
 * custom sound only — a closed app can never play a custom file (see /sw.js).
 */
export function createChimePlayer(url = CHIME_URL, gainValue = 0.85) {
  let ctx = null;
  let buffer = null;
  let unlocked = false;

  const unlock = () => {
    unlocked = true;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && !ctx) ctx = new AC();
      if (ctx && ctx.state === 'suspended') ctx.resume();
      if (ctx && !buffer) {
        fetch(url).then((r) => r.arrayBuffer())
          .then((buf) => ctx.decodeAudioData(buf))
          .then((decoded) => { buffer = decoded; })
          .catch(() => {});
      }
    } catch (_e) { /* noop */ }
  };

  const play = () => {
    if (!ctx || !unlocked || !buffer) return;         // autoplay policy
    try {
      if (ctx.state === 'suspended') ctx.resume();
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(gainValue, ctx.currentTime);
      src.connect(gain); gain.connect(ctx.destination);
      src.start();                                     // plays once (no loop)
    } catch (_e) { /* noop */ }
  };

  return { unlock, play };
}
